import { flatten } from 'lodash';
import { Boundary, Mission, Zone } from '../../db';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import { cleanSet } from '../../utils';
import { alertWarn } from '../../alertDispatcher';
import {
  polyContainsGeometry,
  polyOverlapsGeometry as polyIntersectsGeometry,
  polyOuterBoundaryContainsOtherPolyHoles,
  polyOverlapsOtherPolyHoles,
} from './poly_utils';
import { ComplexPolygonCoords, Poly } from '../../types/types';
import { BOUNDARY_MIN_VALID_AREA_SQR_METERS, MINIMUM_COORDINATES_FOR_VALID_BOUNDARY } from '../../constants';
import { GeoDataUtilities } from '@rogoag/shared-ts';

class PolyManager {
  constructor(
    private mission: Mission,
    private zone: Zone,
  ) {}

  createPoly(polygonCoords: ComplexPolygonCoords): { poly: Poly | null; errors?: string[] } {
    const errors: string[] = [];

    if (polygonCoords.length === 0) {
      errors.push('No polygon coordinates provided');

      return { poly: null, errors };
    }

    // all simple polygons inside a polygon should have the same poly_id
    const polyId = this.mission.assign_id();

    for (const [i, simplePolygonCoords] of Object.entries(polygonCoords)) {
      const isOuterPolygon = parseInt(i) === 0;
      const validBoundary = simplePolygonCoords.length >= MINIMUM_COORDINATES_FOR_VALID_BOUNDARY;

      if (!validBoundary) {
        const errorMessage = `Boundary with index ${i} must have at least ${MINIMUM_COORDINATES_FOR_VALID_BOUNDARY} coordinates, skipping it`;
        errors.push(errorMessage);

        if (isOuterPolygon) {
          return { poly: null, errors };
        }

        continue;
      }

      // Check the area
      const area = GeoDataUtilities.polygonArea(simplePolygonCoords);

      if (!Boundary.isBoundaryAreaValid(area)) {
        errors.push(
          `One of the polygon's area is less than minimal (${Math.round(area).toLocaleString()} < ${BOUNDARY_MIN_VALID_AREA_SQR_METERS} m²), skipping it.`,
        );

        continue;
      }

      const newBoundary = Boundary.create();
      newBoundary.boundary_id = this.mission.assign_id();
      (newBoundary.coordinates = simplePolygonCoords.map((coord) => [coord[1], coord[0]])),
        (newBoundary.poly_id = polyId);

      if (isOuterPolygon) {
        newBoundary.OuterZone_id = this.zone.instance_id;
      } else {
        newBoundary.InnerZone_id = this.zone.instance_id;
      }
    }

    return {
      poly: this.zone.getPolysGroupedByPolyId()[polyId],
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  addPolyToZone(polyId: number) {
    // console.log('PolyManager::addPolyToZone - polyId: ', polyId, ' zone: ', this.zone.zone_name);

    const allMissionOuterBoundaries = cleanSet(
      flatten(this.mission.getZones().map((zone) => zone.getOuterZoneBoundarys())),
    );

    const polyCurrentOuterBoundary = allMissionOuterBoundaries.find((sel) => sel.poly_id === polyId);

    if (!polyCurrentOuterBoundary) {
      return;
    }

    const allMissionInnerBoundaries = cleanSet(
      flatten(this.mission.getZones().map((zone) => zone.getInnerZoneBoundarys())),
    );
    const polyCurrentInnerBoundaries = allMissionInnerBoundaries.filter((sel) => sel.poly_id === polyId);
    const polyCurrentOuterBoundaryGeometry = polyCurrentOuterBoundary.toGeometry();

    const polyIdsThatContain = this.getPolysContainingGeometry(polyCurrentOuterBoundaryGeometry);
    const onlyContainingPolyIsSelf = polyIdsThatContain.length === 1 && polyIdsThatContain[0] === polyId;

    if (onlyContainingPolyIsSelf) {
      // If the containing poly is the same poly that is being added - no action needed.
      // console.log(
      //   `PolyManager - poly "${polyId}" already belongs to zone "${this.zone.zone_name}" - no action needed`,
      // );

      const polyThatIntersects = this.intersectsGeometry(polyCurrentOuterBoundaryGeometry, polyId);

      if (polyThatIntersects !== undefined) {
        alertWarn(`New polygon "${polyId}" intersects the existing one "${polyThatIntersects}"`);
      }

      return;
    }

    // There should be the only outside poly that can contain polyId, so
    // ok to look for the first found element here.
    const outsidePolyThatContains = polyIdsThatContain.find((p) => p !== polyId);

    if (outsidePolyThatContains !== undefined) {
      const polyBeingAdded = this.mission.getZoneByPolyId(polyId)?.getPolysGroupedByPolyId()[polyId];

      if (!polyBeingAdded) {
        alertWarn('Unexpected error: could not find poly being added.');

        return;
      }

      const polyThatContains = this.zone.getPolysGroupedByPolyId()[outsidePolyThatContains];

      // if polyBeingAdded intersects or overlaps any holes of the outsidePolyThatContains,
      // we cannot make it a hole. It will be a separate outside poly.
      const polyIntersectsHoles = polyOverlapsOtherPolyHoles(polyBeingAdded, polyThatContains);

      if (polyIntersectsHoles) {
        alertWarn('New polygon intersects the current zone holes.');

        // We will make it an outer boundary for now.
        this.makeOutsidePoly(polyCurrentOuterBoundary, polyCurrentInnerBoundaries);

        return;
      }

      // if polyBeingAdded's boundary contains  any holes of the outsidePolyThatContains,
      // we cannot make it a hole. It will be a separate outside poly.
      const polyBeingAddedBoundaryContainsOutsidePolyHoles = polyOuterBoundaryContainsOtherPolyHoles(
        polyBeingAdded,
        polyThatContains,
      );

      if (polyBeingAddedBoundaryContainsOutsidePolyHoles) {
        alertWarn('New polygon contains holes of the current zone.');

        // We will make it an outer boundary for now.
        this.makeOutsidePoly(polyCurrentOuterBoundary, polyCurrentInnerBoundaries);

        return;
      }

      console.log('PolyManager - zone contains new poly - make a hole', outsidePolyThatContains);
      this.makeHole(outsidePolyThatContains, polyCurrentOuterBoundary, polyCurrentInnerBoundaries);

      return;
    }

    const polyThatIntersects = this.intersectsGeometry(polyCurrentOuterBoundaryGeometry, polyId);

    if (polyThatIntersects !== undefined) {
      console.warn('PolyManager - A new polygon intersects the current zone');
      alertWarn('New polygon intersects the current zone');

      // We will make it an outer boundary for now.
      this.makeOutsidePoly(polyCurrentOuterBoundary, polyCurrentInnerBoundaries);

      return;
    }

    console.log('PolyManager - new poly is outside of main poly');
    this.makeOutsidePoly(polyCurrentOuterBoundary, polyCurrentInnerBoundaries);
  }

  private makeOutsidePoly(polyCurrentOuterBoundary: Boundary, polyCurrentInnerBoundaries: Boundary[]) {
    const prevZoneIds = new Set<number>(
      [polyCurrentOuterBoundary.OuterZone_id, polyCurrentOuterBoundary.InnerZone_id].filter(Boolean) as [number],
    );
    polyCurrentOuterBoundary.OuterZone_id = this.zone.instance_id;
    polyCurrentOuterBoundary.InnerZone_id = undefined;
    polyCurrentInnerBoundaries.forEach((b) => {
      b.OuterZone_id = undefined;
      b.InnerZone_id = this.zone.instance_id;
    });

    this.checkDegenerateZones(Array.from(prevZoneIds));
  }

  private checkDegenerateZones(zoneInstanceIds: number[]) {
    zoneInstanceIds.forEach((zoneInstanceId: number) => {
      const zone = Zone.get(zoneInstanceId);

      if (!zone) {
        return;
      }

      if (zone.getOuterZoneBoundarys().length === 0) {
        zone.dispose();
      }
    });
  }

  private makeHole(
    polyIdThatContains: number,
    polyCurrentOuterBoundary: Boundary,
    polyCurrentInnerBoundaries: Boundary[],
  ) {
    const prevZoneIds = new Set<number>(
      [polyCurrentOuterBoundary.OuterZone_id, polyCurrentOuterBoundary.InnerZone_id].filter(Boolean) as [number],
    );

    polyCurrentOuterBoundary.OuterZone_id = undefined;
    polyCurrentOuterBoundary.InnerZone_id = this.zone.instance_id;
    polyCurrentOuterBoundary.poly_id = polyIdThatContains;

    polyCurrentInnerBoundaries.forEach((ib) => {
      ib.OuterZone_id = this.zone.instance_id;
      ib.InnerZone_id = undefined;
      ib.poly_id = this.mission.assign_id() * -1;
    });

    this.checkDegenerateZones(Array.from(prevZoneIds));
  }

  private getPolysContainingGeometry(polyGeometry: Feature<Polygon>): number[] {
    const polys = this.zone.getPolysGroupedByPolyId();
    const polyIds = Object.keys(polys);

    if (polyIds.length === 0) {
      return [];
    }

    const out: number[] = [];

    for (const polyId of polyIds) {
      const contained = polyContainsGeometry(polys[polyId], polyGeometry);

      if (contained) {
        out.push(parseInt(polyId));
      }
    }

    return out;
  }

  private intersectsGeometry(geometry: Feature<Polygon | MultiPolygon>, excludePolyId: number): number | undefined {
    const polys = this.zone.getPolysGroupedByPolyId();
    const polyIds = Object.keys(polys).filter((polyId) => parseInt(polyId) !== excludePolyId);

    if (polyIds.length === 0) {
      return undefined;
    }

    for (const polyId of polyIds) {
      const intersects = polyIntersectsGeometry(polys[polyId], geometry);

      if (intersects) {
        return parseInt(polyId);
      }
    }

    return undefined;
  }
}

export default PolyManager;
